import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GetTrendingDebates } from "../../api-requests/Get";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import { UserContext } from "../../context/UserContext";
import Header from "../../components/Header";
import PageLoading from "../../components/PageLoading";

interface DebateOption {
  id: string;
  option_text: string;
  votes: number;
  percentage: number;
}

interface Debate {
  id: string;
  title: string;
  description?: string;
  totalVotes: number;
  totalOptions: number;
  options: DebateOption[];
  userVote: string | null;
}

const DebatesPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [debates, setDebates] = useState<Debate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDebates = async () => {
    try {
      const headers = user?.isloggedin ? getBearerHeaders() : undefined;
      const response = await GetTrendingDebates(headers);
      setDebates(response.debates);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching debates:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebates();
  }, [user?.isloggedin]);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      <Header />
      <div className="min-h-screen  text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Community Debates</h1>
            <p className="text-gray-300 text-lg">
              Join the conversation and share your opinion on the hottest topics
              in hip-hop
            </p>
          </div>

          {/* Debates Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {debates.map((debate) => {
              const hasVoted =
                debate.userVote !== null && debate.userVote !== undefined;
              return (
                <div
                  key={debate.id}
                  className="card-container cursor-pointer group"
                  onClick={() => navigate(`/debate/${debate.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold group-hover:text-blue-400 transition-colors flex-1">
                      {debate.title}
                    </h3>
                    {hasVoted && (
                      <span className="ml-2 shrink-0 bg-green-500/20 text-green-400 border border-green-500/40 px-2 py-0.5 rounded-full text-xs font-medium">
                        ✓ Voted
                      </span>
                    )}
                  </div>

                  {debate.description && (
                    <p className="text-gray-300 mb-4 text-sm line-clamp-2">
                      {debate.description}
                    </p>
                  )}

                  {/* Vote Preview */}
                  <div className="space-y-2 mb-4">
                    {debate.options.slice(0, 2).map((option) => {
                      const isVotedOption = debate.userVote === option.id;
                      return (
                        <div key={option.id} className="relative">
                          <div className="flex justify-between items-center mb-1">
                            <span
                              className={`text-sm font-medium truncate ${isVotedOption ? "text-green-400" : ""}`}
                            >
                              {isVotedOption && "✓ "}
                              {option.option_text}
                            </span>
                            <span className="text-xs text-gray-400">
                              {option.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-600 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                isVotedOption
                                  ? "bg-gradient-to-r from-green-500 to-emerald-500"
                                  : "bg-gradient-to-r from-blue-500 to-purple-500"
                              }`}
                              style={{ width: `${option.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                    {debate.totalOptions > 2 && (
                      <div className="text-xs text-gray-400">
                        +{debate.totalOptions - 2} more option
                        {debate.totalOptions - 2 === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>{debate.totalVotes} total votes</span>
                    <span className="btn-sm group-hover:bg-blue-500 transition-colors">
                      {hasVoted ? "View Results" : "Vote Now"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {debates.length === 0 && !loading && (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold mb-2">No Debates Yet</h3>
              <p className="text-gray-400">Check back soon for new debates!</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DebatesPage;
